import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from './components/ui/sonner';
import './App.css';

// Pages
import LoginPage from './pages/LoginPage';
import SetPasswordPage from './pages/SetPasswordPage';
import SharedStockTake from './pages/SharedStockTake';
import { can, homePath } from './lib/perms';
import RolesPermissions from './pages/settings/RolesPermissions';
import Dashboard from './pages/Dashboard';
import Suppliers from './pages/Suppliers';
import StockItems from './pages/StockItems';
import ParGrid from './pages/ParGrid';
import MenuProducts from './pages/MenuProducts';
import StockTake from './pages/StockTake';
import Orders from './pages/Orders';
import Recipes from './pages/Recipes';
import RecipeDetail from './pages/RecipeDetail';
import RecipeForm from './pages/RecipeForm';
import RecipePrint from './pages/RecipePrint';
import PriceTagPrint from './pages/PriceTagPrint';
import OrderPrint from './pages/OrderPrint';
import Reports from './pages/Reports';
import Layout from './components/Layout';

// Settings sub-pages
import SettingsHub from './pages/Settings';
import BranchesSettings from './pages/settings/BranchesSettings';
import UsersSettings from './pages/settings/UsersSettings';
import PrintersSettings from './pages/settings/PrintersSettings';
import LoyverseSettings from './pages/settings/LoyverseSettings';
import BenchmarkMarginsSettings from './pages/settings/BenchmarkMarginsSettings';
import SupplierDashboard from './pages/settings/SupplierDashboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (userData) => {
    setUser(userData);
    try {
      const me = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(me.data);
    } catch { /* keep login response */ }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowedRoles = [], perm }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if ((allowedRoles.length > 0 && !allowedRoles.includes(user.role)) || (perm && !can(user, perm))) {
    const home = homePath(user);
    return home === location.pathname ? <p className="p-8 text-muted-foreground">You don't have access to this page.</p> : <Navigate to={home} replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/set-password" element={<SetPasswordPage />} />
      <Route path="/count/:token" element={<SharedStockTake />} />
      
      <Route path="/" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<ProtectedRoute perm="dashboard.view"><Dashboard /></ProtectedRoute>} />
        <Route path="supplier-dashboard" element={<ProtectedRoute allowedRoles={['supplier']}><SupplierDashboard /></ProtectedRoute>} />
        <Route path="suppliers" element={<ProtectedRoute perm="suppliers.view"><Suppliers /></ProtectedRoute>} />
        <Route path="stock-items" element={<ProtectedRoute perm="stock_items.view"><StockItems /></ProtectedRoute>} />
        <Route path="par-grid" element={
          <ProtectedRoute perm="par_grid.view">
            <ParGrid />
          </ProtectedRoute>
        } />
        <Route path="menu-products" element={<ProtectedRoute perm="menu.view"><MenuProducts /></ProtectedRoute>} />
        <Route path="price-tags/print" element={<PriceTagPrint />} />
        <Route path="orders/:orderId/print" element={<OrderPrint />} />
        <Route path="stock-take" element={<ProtectedRoute perm="stock_take.view"><StockTake /></ProtectedRoute>} />
        <Route path="orders" element={<ProtectedRoute perm="orders.view"><Orders /></ProtectedRoute>} />
        <Route path="recipes" element={<ProtectedRoute perm="menu.view"><Recipes /></ProtectedRoute>} />
        <Route path="recipes/new" element={<RecipeForm />} />
        <Route path="recipes/print" element={<RecipePrint />} />
        <Route path="recipes/:id/edit" element={<RecipeForm />} />
        <Route path="recipes/:id" element={<RecipeDetail />} />
        <Route path="reports" element={<ProtectedRoute perm="reports.view"><Reports /></ProtectedRoute>} />
        <Route path="settings/roles" element={<ProtectedRoute allowedRoles={['master_admin']}><RolesPermissions /></ProtectedRoute>} />
        
        {/* Settings hub & sub-pages */}
        <Route path="settings" element={<ProtectedRoute allowedRoles={['master_admin', 'manager', 'staff']}><SettingsHub /></ProtectedRoute>} />
        <Route path="settings/branches" element={
          <ProtectedRoute perm="settings.branches">
            <BranchesSettings />
          </ProtectedRoute>
        } />
        <Route path="settings/users" element={
          <ProtectedRoute perm="settings.users">
            <UsersSettings />
          </ProtectedRoute>
        } />
        <Route path="settings/printers" element={<ProtectedRoute perm="settings.printers"><PrintersSettings /></ProtectedRoute>} />
        <Route path="settings/loyverse" element={
          <ProtectedRoute allowedRoles={['master_admin']}>
            <LoyverseSettings />
          </ProtectedRoute>
        } />
        <Route path="settings/benchmark-margins" element={
          <ProtectedRoute allowedRoles={['master_admin']}>
            <BenchmarkMarginsSettings />
          </ProtectedRoute>
        } />
      </Route>

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
